import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface VotingRecord {
  session_id: string;
  experience_categories: string[];
}

// Shared cache for voting data
let cachedData: VotingRecord[] | null = null;
let cacheTime: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useCommunityVotingData = () => {
  const [data, setData] = useState<VotingRecord[] | null>(cachedData);
  const [isLoading, setIsLoading] = useState(!cachedData);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      // Return cached data if still valid
      if (cachedData && cacheTime && Date.now() - cacheTime < CACHE_DURATION) {
        setData(cachedData);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const { data: fetchedData, error: fetchError } = await supabase
          .rpc('get_community_voting_data');

        if (fetchError) throw fetchError;

        // Update cache
        cachedData = fetchedData || [];
        cacheTime = Date.now();
        
        setData(cachedData);
        setError(null);
      } catch (err) {
        setError(err as Error);
        console.error('Error fetching community voting data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    // Subscribe to realtime updates
    const channel = supabase
      .channel('community-votes-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'community_votes'
        },
        () => {
          // Invalidate cache on changes
          cachedData = null;
          cacheTime = null;
          fetchData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return { data, isLoading, error };
};
